import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "@carbon/react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { CompanyContactType } from "../../../types";
import { getContactTypeLabel } from "../companyProfileHelpers";
import { CompanyProfileTabProps } from "./OverviewTab";

const NONE = "-";

interface ContactRow extends DataTableRow {
  name: string;
  position: string;
  contactType: ReactNode;
  email: string;
  phone: string;
}

/**
 * The "Contacts" tab — now a Carbon DataTable (name, position, type, email, phone) instead of a
 * card grid. The empty state keeps its real `<h3>` "No Contacts Added" (rendered directly, not via
 * the shared EmptyState) so `e2e/profile-company.spec.ts`'s heading assertion keeps passing.
 */
const ContactsTab = ({ companyProfile }: CompanyProfileTabProps) => {
  const { t } = useTranslation();
  const contacts = companyProfile.contacts ?? [];

  if (contacts.length === 0) {
    return (
      <div className="company-profile__empty-section">
        <h3>{t("companyProfile.noContactsAdded")}</h3>
        <p>{t("companyProfile.addContactsToProfile")}</p>
      </div>
    );
  }

  const rows: ContactRow[] = contacts.map((contact, index) => ({
    id: contact.id ?? index,
    name: contact.name || NONE,
    position: contact.position || NONE,
    contactType: (
      <Tag type="blue" size="sm">
        {getContactTypeLabel(contact.type as CompanyContactType, t)}
      </Tag>
    ),
    email: contact.email || NONE,
    phone: contact.phone || NONE,
  }));

  const headers: DataTableColumn<ContactRow>[] = [
    { key: "name", header: t("companyProfile.table.name") },
    { key: "position", header: t("companyProfile.table.position") },
    { key: "contactType", header: t("companyProfile.table.contactType") },
    { key: "email", header: t("companyProfile.email") },
    { key: "phone", header: t("companyProfile.phone") },
  ];

  return (
    <div className="company-profile__section">
      <h2 className="company-profile__section-heading">
        {t("companyProfile.contacts")}
      </h2>
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={false}
        empty={<EmptyState title={t("companyProfile.noContactsAdded")} />}
      />
    </div>
  );
};

export default ContactsTab;
